import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ComponentType, FunctionComponent, ReactNode } from 'react';

import { dropAt } from '../utils';
import type { ButtonType } from '../components/generic';

export type DialogType = 'alert' | 'confirm' | 'confirmWithInput';

export type DialogProps = {
  title: string;
  body: string | ReactNode;
  onClose: () => void;
};

export type AlertDialogProps = DialogProps & {
  type: 'alert';
};

export type ConfirmDialogProps = DialogProps & {
  type: 'confirm';
  onConfirm: () => void;
  confirmText?: string;
  confirmBtnType?: ButtonType;
  cancelText?: string;
  allowEnterConfirm?: boolean;
};

export type ConfirmWithInputDialogProps = Omit<ConfirmDialogProps, 'type'> & {
  type: 'confirmWithInput';
  //  Text which the user must input before they are allowed to complete an action
  inputText: string;
};

export type DialogItem =
  | AlertDialogProps
  | ConfirmDialogProps
  | ConfirmWithInputDialogProps;

//  type is only used by this context, and onClose is passed directly/doesn't need to be added by the user.
export type DialogItemProps<DI> = Omit<DI, 'type' | 'onClose'>;

//  Returned from each method is another method which will remove the dialog.
type RemoveDialogCallback = () => void;

//  The methods used throughout the app to "call" into the context.
export type DialogState = {
  state: DialogItem[];
  onAlert: (state: DialogItemProps<AlertDialogProps>) => RemoveDialogCallback;
  onConfirm: (state: DialogItemProps<ConfirmDialogProps>) => RemoveDialogCallback;
  onConfirmWithInput: (state: DialogItemProps<ConfirmWithInputDialogProps>) => RemoveDialogCallback;
  //  [nit] index is brittle/can't be used as a React key. Consider using a uuid.
  onRemoveDialog: (dialogIndex: number) => void;
  //  Returns true if there is any dialog open.
  isAnyDialogOpen: boolean;
};

//  Create the context.
const DialogContext = createContext<DialogState>({} as DialogState);

//  Export hooks for components to consume.
export const useDialogContext = () => useContext<DialogState>(DialogContext);
//  Convenience hooks for the most common methods.
export const useOnAlert = () => useDialogContext().onAlert;
export const useOnConfirm = () => useDialogContext().onConfirm;
export const useOnConfirmWithInput = () => useDialogContext().onConfirmWithInput;

/**
 * Manages the state of any of the dialog elements on the page.
 *
 * @param {ReactNode} children - Children which context will be provided.
 */
const DialogContextProvider: FunctionComponent = ({ children }) => {
  const [state, setState] = useState<DialogItem[]>([]);

  //  Remove a dialog item at a given index.
  const onRemoveDialog = useCallback((dialogIndex: number) => {
    setState((prevState: DialogItem[]) => dropAt(prevState, dialogIndex));
  }, []);

  //  Add a new dialog item to the stack.
  const onAddDialog = useCallback((item: DialogItem) => {
    //  Create an onClose handler.
    const removalIndex = state.length;
    const onClose = () => onRemoveDialog(removalIndex);
    //  If confirmation dialog, we want to call onClose after onConfirm is called.
    const onConfirm = item.type === 'confirm' || item.type === 'confirmWithInput'
      ? () => {
          item.onConfirm();
          onClose();
        }
      : undefined;
    setState((prevState: DialogItem[]) => prevState.concat({ ...item, onClose, onConfirm } as DialogItem));
    //  Return a remove fn bound with the index, in case consumers want to manually close the dialog.
    return onClose;
  }, [state, onRemoveDialog]);

  //  Store a memoized context value for the dialog state.
  const contextValue: DialogState = useMemo(() => ({
    state,
    onAlert: (dialogItem: DialogItemProps<AlertDialogProps>) => onAddDialog({ ...dialogItem, type: 'alert' } as AlertDialogProps),
    onConfirm: (dialogItem: DialogItemProps<ConfirmDialogProps>) => onAddDialog({ ...dialogItem, type: 'confirm' } as ConfirmDialogProps),
    onConfirmWithInput: (dialogItem: DialogItemProps<ConfirmWithInputDialogProps>) => onAddDialog({ ...dialogItem, type: 'confirmWithInput' } as ConfirmWithInputDialogProps),
    onRemoveDialog,
    isAnyDialogOpen: state.length > 0,
  }), [
    state,
    //  Added this to supress warning; these won't change across renders because of useCallback.
    onAddDialog,
    onRemoveDialog,
  ]);

  return (
    <DialogContext.Provider value={contextValue}>
      {children}
    </DialogContext.Provider>
  );
};

/**
 * Higher-order component which will "inject" the dialog context into a provided component.
 * Allows us to `useDialogContext` in class components (because hooks don't work in class components).
 * Note that this is intended to just be used as a stop-gap until we can convert all components to functional,
 * where we can simply useDialogContext directly.
 */
export function WithDialogContext<P extends Record<string, unknown>>(Component: ComponentType<P>) {
  const WrappedComponent: FunctionComponent<P> = (props: P) => {
    const dialogContext = useDialogContext();
    return (
      <Component
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        dialogContext={dialogContext}
      />
    );
  };
  //  Used for debugging only
  WrappedComponent.displayName = `withDialogContext(${Component.displayName || Component.name || 'Component'})`;
  return WrappedComponent;
}

export default DialogContextProvider;
